import React, { useState, useEffect, CSSProperties } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  upsertMerchant,
  fetchUserMerchants,
  selectMerchants,
  selectMerchantLoading,
  Merchant,
} from "../../../redux/merchantSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import countries from "../../../GLOBAL/Country";
import { MAIN_CURRENCIES } from "../../../GLOBAL/Currencies";

/* ─── PALETTE ─── */
const IVORY    = "#F5F0EB";
const CREAM    = "#EDE8E1";
const STONE    = "#D4CFC8";
const WARMGRAY = "#9B958D";
const DUSK     = "#6B6560";
const NIGHT    = "#1A1714";
const PURE_W   = "#FFFFFF";
const PURE_B   = "#000000";
const ROUGE    = "#C8303A";

/* ─── STEPS ─── */
const STEPS = [
  { id: 1, label: "Business", sub: "Company identity" },
  { id: 2, label: "Contact",  sub: "Your details" },
  { id: 3, label: "Confirm",  sub: "Review & register" },
];

const BUSINESS_TYPES = ["individual", "company", "nonprofit", "government"];

/* ─── ANIMATIONS ─── */
const pageVariants = {
  enter:  { opacity: 0, y: 40,  filter: "blur(8px)" },
  center: { opacity: 1, y: 0,   filter: "blur(0px)" },
  exit:   { opacity: 0, y: -30, filter: "blur(6px)" },
};

const staggerContainer = {
  center: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
};

const fadeUp = {
  enter:  { opacity: 0, y: 20 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};

const scaleIn = {
  enter:  { opacity: 0, scale: 0.92 },
  center: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};

/* ─── GOOGLE FONT INJECTION ─── */
const fontHref =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Libre+Franklin:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const link = document.createElement("link");
  link.rel   = "stylesheet";
  link.href  = fontHref;
  document.head.appendChild(link);
}

type ViewMode = "list" | "form";

/* ═══════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════ */
export default function MerchantOnboarding() {
  const dispatch     = useDispatch<AppDispatch>();
  const navigate     = useNavigate();

  // ─── Auth comes from Redux — NO direct Firebase calls ───
  // authSlice stores the Firebase user as user.userId (= firebase uid)
  const user         = useSelector((s: RootState) => s.auth.user);
  const merchants    = useSelector(selectMerchants);
  const storeLoading = useSelector(selectMerchantLoading);

  const [view, setView]         = useState<ViewMode>("list");
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);
  const [fetched, setFetched]   = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 800 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── Fetch merchants using userId from authSlice ───
  // authSlice stores userId as user.userId (the Firebase Auth UID)
  useEffect(() => {
    const uid = user?.userId;
    if (uid && !fetched) {
      dispatch(fetchUserMerchants(uid))
        .unwrap()
        .then(() => setFetched(true))
        .catch(() => setFetched(true));
    }
  }, [user?.userId, dispatch, fetched]);

  const [form, setForm] = useState({
    businessName: "",
    businessType: "company",
    country:      countries.find((c) => c.code === "DE")!,
    currency:     MAIN_CURRENCIES.find((c) => c.code === "EUR")!,
    email:        "",
    phone:        "",
    website:      "",
    metadata:     {} as Record<string, any>,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const resetForm = () => {
    setForm({
      businessName: "",
      businessType: "company",
      country:      countries.find((c) => c.code === "DE")!,
      currency:     MAIN_CURRENCIES.find((c) => c.code === "EUR")!,
      email:        "",
      phone:        "",
      website:      "",
      metadata:     {},
    });
    setStep(1);
    setError(null);
    setSuccess(false);
  };

  /* ─── SUBMIT ─── */
  const handleSubmit = async () => {
    // ─── user.userId is the Firebase Auth UID stored by authSlice ───
    const uid = user?.userId;
    if (!uid) {
      setError("Please sign in to register a merchant account.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      await dispatch(
        upsertMerchant({
          userId:       uid,              // ✅ stored as `userId` field in each merchant doc
          businessName: form.businessName,
          businessType: form.businessType,
          country:      form.country.code,
          currency:     form.currency.code,
          email:        form.email || user?.email || "",
          phone:        form.phone,
          website:      form.website,
          metadata:     form.metadata,
        })
      ).unwrap();

      setSuccess(true);

      // Re-fetch after creation so list updates
      await dispatch(fetchUserMerchants(uid)).unwrap();

      setTimeout(() => {
        resetForm();
        setView("list");
      }, 1800);
    } catch (err: any) {
      console.error(err);
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ─── SHARED STYLES ─── */
  const serif: CSSProperties = { fontFamily: "'Cormorant Garamond', 'Georgia', serif" };
  const sans: CSSProperties  = { fontFamily: "'Libre Franklin', 'Helvetica Neue', sans-serif" };

  const inputBase: CSSProperties = {
    ...sans, width: "100%", background: PURE_W, border: `1px solid ${STONE}`, borderRadius: 0,
    padding: "14px 18px", fontSize: 14, fontWeight: 400, color: NIGHT, outline: "none",
    boxSizing: "border-box" as const, transition: "border-color 0.3s, box-shadow 0.3s", letterSpacing: "0.02em",
  };

  const labelStyle: CSSProperties = {
    ...sans, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", color: WARMGRAY,
    textTransform: "uppercase" as const, marginBottom: 8, display: "block",
  };

  const fieldGroup: CSSProperties = { marginBottom: 24 };

  const primaryBtn: CSSProperties = {
    ...sans, width: "100%", padding: "18px 0", borderRadius: 0, border: `2px solid ${PURE_B}`,
    background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 12, cursor: "pointer",
    letterSpacing: "0.22em", textTransform: "uppercase" as const, transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
  };

  const secondaryBtn: CSSProperties = {
    ...sans, padding: "18px 0", borderRadius: 0, border: `1px solid ${STONE}`, background: "transparent",
    color: DUSK, fontWeight: 500, fontSize: 12, cursor: "pointer",
    letterSpacing: "0.16em", textTransform: "uppercase" as const, transition: "all 0.3s",
  };

  const focusHandler = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = PURE_B; e.target.style.boxShadow = `0 0 0 1px ${PURE_B}`;
  };
  const blurHandler = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = STONE; e.target.style.boxShadow = "none";
  };
  const hoverPrimary = {
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = PURE_B; },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = PURE_B; e.currentTarget.style.color = PURE_W; },
  };
  const hoverSecondary = {
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = PURE_B; e.currentTarget.style.color = PURE_B; },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = STONE; e.currentTarget.style.color = DUSK; },
  };

  /* ═══════════════════════════════════════
     LIST VIEW
     ═══════════════════════════════════════ */
  if (view === "list") {
    return (
      <div style={{ minHeight: "100vh", background: IVORY, ...sans }}>
        {/* Header */}
        <div style={{ background: PURE_B, padding: isMobile ? "28px 24px" : "36px 56px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ ...serif, fontSize: isMobile ? 24 : 30, fontWeight: 300, letterSpacing: "0.28em", color: PURE_W, lineHeight: 1 }}>COSMOOLA</div>
            <div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, marginTop: 6, textTransform: "uppercase" as const, fontWeight: 500 }}>Your Organisations</div>
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => { resetForm(); setView("form"); }}
            style={{ ...sans, padding: isMobile ? "12px 20px" : "14px 32px", borderRadius: 0, border: `1.5px solid ${PURE_W}`, background: "transparent", color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.18em", textTransform: "uppercase" as const, display: "flex", alignItems: "center", gap: 10 }}
          >
            <span style={{ fontSize: 18, fontWeight: 300, lineHeight: 1 }}>+</span>
            {isMobile ? "Add" : "Add Company"}
          </motion.button>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 960, margin: "0 auto", padding: isMobile ? "32px 20px" : "52px 40px" }}>
          {storeLoading && !fetched && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: 60, color: WARMGRAY, ...sans, fontSize: 14 }}>
              Loading your organisations...
            </motion.div>
          )}

          {fetched && merchants.length === 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ textAlign: "center", padding: isMobile ? "60px 20px" : "100px 40px" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", border: `1.5px solid ${STONE}`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 28, fontSize: 28, color: STONE }}>◇</div>
              <h2 style={{ ...serif, fontSize: isMobile ? 28 : 36, fontWeight: 300, color: NIGHT, margin: "0 0 12px", fontStyle: "italic" }}>No organisations yet</h2>
              <p style={{ ...sans, color: DUSK, fontSize: 14, lineHeight: 1.7, fontWeight: 300, marginBottom: 36, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
                Create your first business to start accepting payments with Cosmoola.
              </p>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={() => { resetForm(); setView("form"); }} style={{ ...primaryBtn, width: "auto", padding: "16px 48px", display: "inline-block" }} {...hoverPrimary}>
                Create Organisation
              </motion.button>
            </motion.div>
          )}

          {merchants.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : merchants.length === 1 ? "1fr" : "1fr 1fr", gap: isMobile ? 16 : 24 }}>
              <AnimatePresence>
                {merchants.map((m, i) => (
                  <MerchantCard
                    key={m.merchantId}
                    merchant={m}
                    index={i}
                    isMobile={isMobile}
                    serif={serif}
                    sans={sans}
                    // ─── Navigate using userId stored in the merchant doc ───
                    onSetUp={() => navigate(`/organizationoverview/${m.userId}`)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════
     FORM VIEW
     ═══════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: IVORY, display: "flex", flexDirection: isMobile ? "column" : "row" as any, ...sans }}>
      {/* LEFT PANEL */}
      <div style={{ width: isMobile ? "100%" : 360, minHeight: isMobile ? "auto" : "100vh", background: PURE_B, padding: isMobile ? "28px 24px 20px" : "56px 44px", display: "flex", flexDirection: "column", position: isMobile ? "relative" : "sticky" as any, top: 0, ...(isMobile ? {} : { height: "100vh" }) }}>
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} style={{ marginBottom: isMobile ? 20 : 56 }}>
          <button onClick={() => { resetForm(); setView("list"); }} style={{ ...sans, background: "transparent", border: "none", color: WARMGRAY, fontSize: 11, letterSpacing: "0.14em", cursor: "pointer", padding: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 6, textTransform: "uppercase" as const, fontWeight: 500 }}>
            ← Back to list
          </button>
          <div style={{ ...serif, fontSize: isMobile ? 26 : 34, fontWeight: 300, letterSpacing: "0.28em", color: PURE_W, lineHeight: 1 }}>COSMOOLA</div>
          <div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, marginTop: 8, textTransform: "uppercase" as const, fontWeight: 500 }}>New Organisation</div>
        </motion.div>

        {!isMobile && (
          <div style={{ flex: 1 }}>
            <div style={{ ...sans, fontSize: 8, letterSpacing: "0.4em", color: DUSK, textTransform: "uppercase" as const, marginBottom: 36, fontWeight: 600 }}>Registration</div>
            {STEPS.map((s, i) => {
              const done = step > s.id; const active = step === s.id;
              return (
                <motion.div key={s.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12, duration: 0.6 }} style={{ display: "flex", alignItems: "flex-start", marginBottom: 32 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginRight: 20, minWidth: 24 }}>
                    <motion.div animate={{ scale: active ? 1 : 0.85, background: done ? PURE_W : "transparent", borderColor: done ? PURE_W : active ? PURE_W : "#444" }} transition={{ duration: 0.4 }} style={{ width: 24, height: 24, borderRadius: "50%", border: `1.5px solid ${done ? PURE_W : active ? PURE_W : "#444"}`, display: "flex", alignItems: "center", justifyContent: "center", ...sans, fontSize: done ? 11 : 10, fontWeight: 600, color: done ? PURE_B : active ? PURE_W : "#555" }}>
                      {done ? "✓" : s.id}
                    </motion.div>
                    {i < STEPS.length - 1 && <motion.div animate={{ background: done ? PURE_W : "#2a2a2a" }} transition={{ duration: 0.5 }} style={{ width: 1, height: 32, marginTop: 6 }} />}
                  </div>
                  <div style={{ paddingTop: 1 }}>
                    <div style={{ ...serif, fontSize: active ? 18 : 16, fontWeight: active ? 500 : 300, color: active ? PURE_W : done ? CREAM : "#555", letterSpacing: "0.06em", transition: "all 0.4s", fontStyle: "italic" }}>{s.label}</div>
                    <div style={{ ...sans, fontSize: 10, color: active ? WARMGRAY : "#3a3a3a", marginTop: 3, letterSpacing: "0.08em", fontWeight: 400 }}>{s.sub}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {isMobile && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {STEPS.map((s) => (
              <motion.div key={s.id} animate={{ flex: step >= s.id ? 2 : 1, background: step >= s.id ? PURE_W : "#333" }} transition={{ duration: 0.5 }} style={{ height: 2, borderRadius: 1 }} />
            ))}
          </div>
        )}

        {!isMobile && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} style={{ borderTop: "1px solid #222", paddingTop: 28 }}>
            <div style={{ ...serif, fontSize: 13, color: "#555", lineHeight: 1.8, fontStyle: "italic" }}>"Simplicity is the ultimate<br />sophistication."</div>
            <div style={{ ...sans, fontSize: 9, letterSpacing: "0.2em", color: "#444", marginTop: 8, textTransform: "uppercase" as const }}>— Leonardo da Vinci</div>
          </motion.div>
        )}
      </div>

      {/* RIGHT PANEL */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "40px 24px 60px" : "60px 48px", minHeight: isMobile ? "auto" : "100vh" }}>
        <div style={{ width: "100%", maxWidth: 520 }}>
          <AnimatePresence mode="wait">

            {/* STEP 1 */}
            {step === 1 && !success && (
              <motion.div key="s1" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                <motion.div variants={staggerContainer} initial="enter" animate="center">
                  <motion.div variants={fadeUp}><div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, textTransform: "uppercase" as const, marginBottom: 14, fontWeight: 600 }}>Step 01 / 03</div></motion.div>
                  <motion.div variants={fadeUp}><h1 style={{ ...serif, fontSize: isMobile ? 36 : 44, fontWeight: 300, color: NIGHT, margin: "0 0 6px", letterSpacing: "-0.01em", lineHeight: 1.1 }}>Business Details</h1></motion.div>
                  <motion.div variants={fadeUp}><p style={{ ...sans, color: DUSK, fontSize: 14, marginBottom: 44, lineHeight: 1.7, fontWeight: 300, maxWidth: 400 }}>Tell us about the business you're registering.</p></motion.div>

                  <motion.div variants={fadeUp} style={fieldGroup}>
                    <label style={labelStyle}>Business Name</label>
                    <input value={form.businessName} onChange={(e) => set("businessName", e.target.value)} placeholder="e.g. Acme Corp" style={inputBase} onFocus={focusHandler} onBlur={blurHandler} />
                  </motion.div>

                  <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
                    <div>
                      <label style={labelStyle}>Business Type</label>
                      <select value={form.businessType} onChange={(e) => set("businessType", e.target.value)} style={{ ...inputBase, appearance: "none" as const, cursor: "pointer" }} onFocus={focusHandler as any} onBlur={blurHandler as any}>
                        {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Country</label>
                      <select value={form.country.code} onChange={(e) => set("country", countries.find((c) => c.code === e.target.value)!)} style={{ ...inputBase, appearance: "none" as const, cursor: "pointer" }} onFocus={focusHandler as any} onBlur={blurHandler as any}>
                        {countries.map((c) => <option key={c.code} value={c.code}>{c.emoji} {c.name}</option>)}
                      </select>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeUp} style={fieldGroup}>
                    <label style={labelStyle}>Default Currency</label>
                    <select value={form.currency.code} onChange={(e) => set("currency", MAIN_CURRENCIES.find((c) => c.code === e.target.value)!)} style={{ ...inputBase, appearance: "none" as const, cursor: "pointer" }} onFocus={focusHandler as any} onBlur={blurHandler as any}>
                      {MAIN_CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>)}
                    </select>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <button onClick={() => { if (!form.businessName.trim()) { setError("Business name is required."); return; } setError(null); setStep(2); }} style={primaryBtn} {...hoverPrimary}>Continue</button>
                  </motion.div>
                  {error && <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 16, color: ROUGE, fontSize: 13, ...sans }}>{error}</motion.div>}
                </motion.div>
              </motion.div>
            )}

            {/* STEP 2 */}
            {step === 2 && !success && (
              <motion.div key="s2" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                <motion.div variants={staggerContainer} initial="enter" animate="center">
                  <motion.div variants={fadeUp}><div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, textTransform: "uppercase" as const, marginBottom: 14, fontWeight: 600 }}>Step 02 / 03</div></motion.div>
                  <motion.div variants={fadeUp}><h1 style={{ ...serif, fontSize: isMobile ? 36 : 44, fontWeight: 300, color: NIGHT, margin: "0 0 6px", letterSpacing: "-0.01em", lineHeight: 1.1 }}>Contact Info</h1></motion.div>
                  <motion.div variants={fadeUp}><p style={{ ...sans, color: DUSK, fontSize: 14, marginBottom: 44, lineHeight: 1.7, fontWeight: 300, maxWidth: 400 }}>How can we reach you?</p></motion.div>

                  <motion.div variants={fadeUp} style={fieldGroup}>
                    <label style={labelStyle}>Business Email</label>
                    <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="hello@acme.com" style={inputBase} onFocus={focusHandler} onBlur={blurHandler} />
                  </motion.div>
                  <motion.div variants={fadeUp} style={fieldGroup}>
                    <label style={labelStyle}>Phone Number</label>
                    <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 000 0000" style={inputBase} onFocus={focusHandler} onBlur={blurHandler} />
                  </motion.div>
                  <motion.div variants={fadeUp} style={fieldGroup}>
                    <label style={labelStyle}>Website <span style={{ color: STONE, fontWeight: 400, letterSpacing: "0.1em" }}>(optional)</span></label>
                    <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://acme.com" style={inputBase} onFocus={focusHandler} onBlur={blurHandler} />
                  </motion.div>

                  <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                    <button onClick={() => setStep(1)} style={secondaryBtn} {...hoverSecondary}>Back</button>
                    <button onClick={() => { if (!form.email.trim()) { setError("Email is required."); return; } setError(null); setStep(3); }} style={primaryBtn} {...hoverPrimary}>Continue</button>
                  </motion.div>
                  {error && <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 16, color: ROUGE, fontSize: 13, ...sans }}>{error}</motion.div>}
                </motion.div>
              </motion.div>
            )}

            {/* STEP 3 */}
            {step === 3 && !success && (
              <motion.div key="s3" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                <motion.div variants={staggerContainer} initial="enter" animate="center">
                  <motion.div variants={fadeUp}><div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, textTransform: "uppercase" as const, marginBottom: 14, fontWeight: 600 }}>Step 03 / 03</div></motion.div>
                  <motion.div variants={fadeUp}><h1 style={{ ...serif, fontSize: isMobile ? 36 : 44, fontWeight: 300, color: NIGHT, margin: "0 0 6px", letterSpacing: "-0.01em", lineHeight: 1.1 }}>Confirm & Register</h1></motion.div>
                  <motion.div variants={fadeUp}><p style={{ ...sans, color: DUSK, fontSize: 14, marginBottom: 36, lineHeight: 1.7, fontWeight: 300, maxWidth: 400 }}>Review your details, then register your organisation.</p></motion.div>

                  <motion.div variants={scaleIn} style={{ background: PURE_W, border: `1px solid ${CREAM}`, padding: isMobile ? 20 : 28, marginBottom: 28 }}>
                    {([
                      ["Business",  form.businessName],
                      ["Type",      form.businessType.charAt(0).toUpperCase() + form.businessType.slice(1)],
                      ["Country",   `${form.country.emoji}  ${form.country.name}`],
                      ["Currency",  `${form.currency.flag}  ${form.currency.code}`],
                      ["Email",     form.email],
                      ["Phone",     form.phone || "—"],
                      ["Website",   form.website || "—"],
                    ] as [string, string][]).map(([k, v], i) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < 6 ? `1px solid ${CREAM}` : "none" }}>
                        <span style={{ ...sans, fontSize: 10, color: WARMGRAY, letterSpacing: "0.14em", textTransform: "uppercase" as const, fontWeight: 600 }}>{k}</span>
                        <span style={{ ...sans, fontSize: 13, color: NIGHT, fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                  </motion.div>

                  <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <button onClick={() => setStep(2)} style={secondaryBtn} {...hoverSecondary}>Back</button>
                    <button onClick={handleSubmit} disabled={loading} style={{ ...primaryBtn, background: loading ? STONE : PURE_B, borderColor: loading ? STONE : PURE_B, color: loading ? DUSK : PURE_W, cursor: loading ? "wait" : "pointer" }} onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = PURE_B; } }} onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.background = PURE_B; e.currentTarget.style.color = PURE_W; } }}>
                      {loading ? "Registering..." : "Register"}
                    </button>
                  </motion.div>
                  {error && <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 16, color: ROUGE, fontSize: 13, ...sans }}>{error}</motion.div>}
                </motion.div>
              </motion.div>
            )}

            {/* SUCCESS */}
            {success && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} style={{ textAlign: "center", paddingTop: isMobile ? 40 : 80 }}>
                <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.15, type: "spring", stiffness: 160, damping: 14 }} style={{ width: 80, height: 80, borderRadius: "50%", border: `2px solid ${PURE_B}`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 32 }}>
                  <motion.svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={PURE_B} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <motion.path d="M5 13l4 4L19 7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }} />
                  </motion.svg>
                </motion.div>
                <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} style={{ ...serif, fontSize: isMobile ? 32 : 42, fontWeight: 300, color: NIGHT, margin: "0 0 12px", fontStyle: "italic" }}>Organisation Created</motion.h1>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} style={{ ...sans, color: DUSK, fontSize: 14, lineHeight: 1.7, fontWeight: 300 }}>Returning to your organisations...</motion.p>
                <motion.div style={{ width: 120, height: 2, background: CREAM, margin: "32px auto 0", overflow: "hidden", borderRadius: 1 }}>
                  <motion.div initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ delay: 0.4, duration: 1.4, ease: "linear" }} style={{ height: "100%", background: PURE_B }} />
                </motion.div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MERCHANT CARD
   ═══════════════════════════════════════ */
function MerchantCard({
  merchant, index, isMobile, serif, sans, onSetUp,
}: {
  merchant: Merchant; index: number; isMobile: boolean;
  serif: CSSProperties; sans: CSSProperties; onSetUp: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const statusColor = merchant.status === "active" ? "#4A7C59" : merchant.status === "pending" ? "#B8860B" : "#C8303A";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: "#FFFFFF", border: `1px solid ${hovered ? "#1A1714" : "#EDE8E1"}`, padding: isMobile ? 24 : 32, cursor: "default", transition: "border-color 0.3s, box-shadow 0.3s", boxShadow: hovered ? "0 8px 32px rgba(0,0,0,0.06)" : "none", display: "flex", flexDirection: "column", gap: 20 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...serif, fontSize: isMobile ? 22 : 26, fontWeight: 400, color: "#1A1714", lineHeight: 1.2, letterSpacing: "-0.01em" }}>{merchant.businessName || "Untitled"}</div>
          <div style={{ ...sans, fontSize: 11, color: "#9B958D", marginTop: 6, letterSpacing: "0.08em" }}>{merchant.businessType.charAt(0).toUpperCase() + merchant.businessType.slice(1)} · {merchant.country} · {merchant.currency.toUpperCase()}</div>
        </div>
        <div style={{ ...sans, fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase" as const, color: statusColor, border: `1px solid ${statusColor}33`, padding: "5px 12px", background: `${statusColor}08` }}>{merchant.status}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {[
          { label: "Email",  val: merchant.email || "—",                         color: undefined, mono: false },
          { label: "Mode",   val: merchant.mode === "live" ? "Live" : "Test",     color: undefined, mono: false },
          { label: "ID",     val: merchant.merchantId.slice(0, 16) + "...",        color: undefined, mono: true  },
          { label: "KYC",    val: merchant.kycVerified ? "Verified" : "Pending",  color: merchant.kycVerified ? "#4A7C59" : "#9B958D", mono: false },
        ].map(({ label, val, color, mono }) => (
          <div key={label}>
            <div style={{ ...sans, fontSize: 9, letterSpacing: "0.16em", color: "#D4CFC8", textTransform: "uppercase" as const, fontWeight: 600, marginBottom: 4 }}>{label}</div>
            <div style={{ ...sans, fontSize: 12, color: color || "#6B6560", fontWeight: color ? 500 : 400, fontFamily: mono ? "'Courier New', monospace" : undefined }}>{val}</div>
          </div>
        ))}
      </div>

      <motion.button
        whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={onSetUp}
        style={{ ...sans, width: "100%", padding: "14px 0", borderRadius: 0, border: "2px solid #1A1714", background: hovered ? "#1A1714" : "transparent", color: hovered ? "#FFFFFF" : "#1A1714", fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.2em", textTransform: "uppercase" as const, transition: "all 0.35s cubic-bezier(0.22,1,0.36,1)", marginTop: 4 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#1A1714"; e.currentTarget.style.color = "#FFFFFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#1A1714"; }}
      >
        Set Up →
      </motion.button>
    </motion.div>
  );
}